import {
  ConversationHoverActions,
  ReplyAction,
} from '@modules/conversation-message/ConversationHoverActions';
import { ConversationMessageOptions } from '@modules/conversation-message/ConversationMessageOptions';
import { ConversationMessageReactions } from '@modules/conversation-message/ConversationMessageReactions';
import { ConversationMessageReply } from '@modules/conversation-message/ConversationMessageReply';
import { UserProfileShortPopoverTrigger } from '@modules/user/UserProfileShortPopoverTrigger';
import { Box, IconButton, Stack, styled } from '@mui/material';
import type { Editor } from '@tiptap/react';
import classNames from 'classnames';
import { bindTrigger, usePopupState } from 'material-ui-popup-state/hooks';
import { memo, useMemo, useState } from 'react';
import { IChatConversation } from 'src/@types/chat';
import {
  ConversationMessageContentType,
  IConversationMessage,
} from 'src/@types/conversation';
import { EmojiPopover } from 'src/components/EmojiPickerPopover';
import { CPAvatar } from 'src/components/base/CPAvatar';
import { Iconify } from 'src/components/iconify';
import { GatheringChatEditInput } from './GatheringChatEditInput';

import { ConversationMessageContent } from '@modules/conversation-message/ConversationMessageContent';
import { StyledActionsBox } from '@modules/conversation/ConversationVirtuoso/shared/ConversationActionsBox';
import { StyledAvatarContainer } from '@modules/conversation/ConversationVirtuoso/shared/ConversationAvatarContainer';
import { StyledMessageAvatarContainer } from '@modules/conversation/ConversationVirtuoso/shared/ConversationMessageAvatarContainer';
import { StyledMessageContainer } from '@modules/conversation/ConversationVirtuoso/shared/ConversationMessageContainer';
import { StyledMessageTime } from '@modules/conversation/ConversationVirtuoso/shared/ConversationMessageTime';
import { StyledSenderName } from '@modules/conversation/ConversationVirtuoso/shared/ConversationSenderName';
import { useMessageMediaContent } from '@modules/conversation/state/ConversationMedia';
import { saveFile } from '@modules/file-menagement/fileUtils';
import { useAuthContext } from 'src/auth/useAuthContext';
import { useWorkspaceUser } from 'src/hooks/useWorkspace';
import { useConversationContext } from 'src/provider/ConversationContext';
import { FormatTime } from 'src/utils/formatTime';
import {
  useGatheringChatMessageReactMutation,
  useGatheringChatMessageRemoveMutation,
} from '../mutations';
import { useGatheringChatContext } from '../useGatheringChatContext';

type Props = {
  message: IConversationMessage & {
    isSameAuthorAsPreviousMessage?: boolean;
    isSameDayAsPreviousMessage?: boolean;
  };
  // conversation: IChatConversation;
  hideAuthor?: boolean;
  disableActions?: boolean;
  editor: Editor;
  updateMessage?: (message: IChatConversation) => void;
};

export const GatheringChatItemInner = ({
  message,
  hideAuthor,
  disableActions,
  updateMessage,
  editor,
}: Props) => {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isOpened, setIsOpened] = useState(false);

  const { user } = useAuthContext();

  const { sessionRef } = useGatheringChatContext();

  const { setReplyTo, goToMessage } = useConversationContext();
  const { mediaContent, getMediaContent } = useMessageMediaContent(message);

  const { reactGatheringChatMessage } = useGatheringChatMessageReactMutation();
  const { removeGatheringChatMessage } =
    useGatheringChatMessageRemoveMutation();

  const sender = useWorkspaceUser(message.author);

  const firstName = useMemo(
    () => sender?.name && sender?.name.split(' ')[0],
    [sender?.name],
  );

  const optionState = usePopupState({
    variant: 'popover',
    popupId: 'demoPopover',
  });

  const handleSendReaction = async (emoji: string) => {
    if (!emoji) return;
    await reactGatheringChatMessage({
      messageId: message._id,
      reaction: emoji,
      sessionRef,
    });
  };

  const handleReplyMessage = (msg: IConversationMessage) => {
    setReplyTo(msg);
    editor!.commands.focus();
  };

  const handleDeleteMessage = async () => {
    await removeGatheringChatMessage({
      sessionRef,
      _id: message._id,
    });
  };

  const handleDownload = async () => {
    const mediaFile = await getMediaContent(message);
    saveFile(message.fileName as string, URL.createObjectURL(mediaFile as any));
  };

  const renderOptions =
    user?._id === sender?._id ||
    message.contentType !== ConversationMessageContentType.TEXT;

  return (
    <StyledActionsBox
      position="relative"
      className={classNames({
        showActions: isOpened || (optionState?.isOpen && !isEditing),
        goToMessage: goToMessage === message._id,
      })}
    >
      {!disableActions && (
        <ConversationHoverActions>
          <EmojiPopover
            onSelectEmoji={(emoji) => handleSendReaction(emoji.shortcodes)}
            arrow="right-center"
            icon="fluent:emoji-add-16-regular"
            isOpened={setIsOpened}
          />
          <ReplyAction
            onClick={() => {
              handleReplyMessage(message);
            }}
          />
          {renderOptions && (
            <>
              <IconButton size="small" {...bindTrigger(optionState)}>
                <Iconify icon="tabler:dots" />
              </IconButton>

              <ConversationMessageOptions
                isAuthor={user?._id === sender?._id}
                state={optionState}
                onDelete={handleDeleteMessage}
                onDownload={mediaContent ? handleDownload : undefined}
                onEdit={() => setIsEditing(true)}
              />
            </>
          )}
        </ConversationHoverActions>
      )}
      <StyledMessageAvatarContainer>
        {!hideAuthor && sender ? (
          <StyledAvatarContainer>
            <UserProfileShortPopoverTrigger userId={sender._id}>
              <CPAvatar
                picture={sender.picture}
                name={sender.name}
                size="xSmall"
              />
            </UserProfileShortPopoverTrigger>
          </StyledAvatarContainer>
        ) : (
          <StyledMessageTime className="message-time">
            {FormatTime.Time(message.createdAt)}
          </StyledMessageTime>
        )}
        <Box>
          {!hideAuthor && sender && (
            <StyledSenderName>
              {firstName}
              <StyledMessageTime>
                {FormatTime.Time(message.createdAt)}
              </StyledMessageTime>
            </StyledSenderName>
          )}

          {message.replyTo && (
            <ConversationMessageReply
              message={message.replyTo}
              editor={editor!}
            />
          )}
          <StyledMessageContainer>
            {isEditing ? (
              <GatheringChatEditInput
                message={message}
                cancelEdition={() => setIsEditing(false)}
              />
            ) : (
              <StyledConversationMessageContent
                message={message}
                editor={editor!}
              />
            )}
          </StyledMessageContainer>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            gap={1}
          >
            <Box>
              {!!message.reactions?.length && (
                <ConversationMessageReactions
                  reactions={message.reactions}
                  sendReaction={handleSendReaction}
                />
              )}
            </Box>
          </Stack>
        </Box>
      </StyledMessageAvatarContainer>
    </StyledActionsBox>
  );
};

export const GatheringChatItem = memo(
  GatheringChatItemInner,
  (prevProps, nextProps) =>
    prevProps.message._id === nextProps.message._id &&
    prevProps.message.content === nextProps.message.content &&
    prevProps.message.reactions === nextProps.message.reactions &&
    prevProps.message.mediaContent === nextProps.message.mediaContent &&
    prevProps.message.isSameAuthorAsPreviousMessage ===
      nextProps.message.isSameAuthorAsPreviousMessage &&
    prevProps.message.isSameDayAsPreviousMessage ===
      nextProps.message.isSameDayAsPreviousMessage,
);

export const StyledConversationMessageContent = styled(
  ConversationMessageContent,
)(({ theme }) => ({
  fontSize: theme.typography.caption.fontSize,
}));
